import { z } from 'zod';
import { screenIdSchema, screenParamsSchemas } from '../screens';

// Closed vocabularies for the notification pipeline. Each mirrors the push
// pattern (a governed `as const` source array feeding both the DB text+CHECK
// and the wire Zod schema, so the constraint and the contract cannot drift).

// Lifecycle of a notification_intents row. `scheduled` → claimed → validated →
// sent is the happy path; `deferred` is a reschedule (kill-switch/cap), and
// `superseded`/`cancelled`/`failed` are terminal off-ramps.
export const NOTIFICATION_INTENT_STATUS = [
  'scheduled',
  'claimed',
  'validated',
  'sent',
  'deferred',
  'superseded',
  'cancelled',
  'failed',
] as const;
export const notificationIntentStatusSchema = z.enum(NOTIFICATION_INTENT_STATUS);

// Per-device delivery bookkeeping for a push (`notification_deliveries`). The
// ticket is the vendor's answer to the send request; the receipt is its later
// answer about the hand-off to APNs / FCM, which is where credential and
// payload failures surface.
export const NOTIFICATION_DELIVERY_TICKET_STATUS = ['ok', 'error'] as const;
export const notificationDeliveryTicketStatusSchema = z.enum(NOTIFICATION_DELIVERY_TICKET_STATUS);
export type NotificationDeliveryTicketStatus = z.infer<
  typeof notificationDeliveryTicketStatusSchema
>;

// `pending` waits for the receipt pass; `unknown` means the vendor never
// published a receipt within the retry budget; `not_applicable` is a send whose
// ticket failed, so there is no receipt to ask for.
export const NOTIFICATION_DELIVERY_RECEIPT_STATUS = [
  'pending',
  'ok',
  'error',
  'unknown',
  'not_applicable',
] as const;
export const notificationDeliveryReceiptStatusSchema = z.enum(NOTIFICATION_DELIVERY_RECEIPT_STATUS);
export type NotificationDeliveryReceiptStatus = z.infer<
  typeof notificationDeliveryReceiptStatusSchema
>;

// The metric-tag vocabulary for a failed receipt: the receipt errors worth
// telling apart on a dashboard, everything else folded into `other`. snake_case
// because Datadog lowercases tag values, so the vendor's camel case would read
// as one run-together word.
export const PUSH_RECEIPT_FAILURE_REASON = [
  'device_not_registered',
  'invalid_credentials',
  'message_too_big',
  'message_rate_exceeded',
  'other',
] as const;
export const pushReceiptFailureReasonSchema = z.enum(PUSH_RECEIPT_FAILURE_REASON);
export type PushReceiptFailureReason = z.infer<typeof pushReceiptFailureReasonSchema>;

// How a tapped notification resolved into session context. Shared rather than
// app-local because two independent consumers must agree on it: the resolver
// that produces the value, and the analytics forwarder's property registry
// that decides whether it is chartable. A vocabulary owned by one of them is a
// vocabulary the other can silently drift from.
export const NOTIFICATION_LAUNCH_OUTCOME = ['seeded', 'out_of_window', 'degraded'] as const;
export const notificationLaunchOutcomeSchema = z.enum(NOTIFICATION_LAUNCH_OUTCOME);
export type NotificationLaunchOutcome = z.infer<typeof notificationLaunchOutcomeSchema>;

// Coarse latency bands between send and open. Bands, never an instant: the
// chart question is "within the hour or the next morning", and a raw timestamp
// on an event that leaves the VPC is a member-correlatable value for no gain.
export const NOTIFICATION_OPEN_LATENCY_BUCKET = [
  '0-5m',
  '5-30m',
  '30m-2h',
  '2-12h',
  '12h+',
  'unknown',
] as const;
export const notificationOpenLatencyBucketSchema = z.enum(NOTIFICATION_OPEN_LATENCY_BUCKET);
export type NotificationOpenLatencyBucket = z.infer<typeof notificationOpenLatencyBucketSchema>;

// How the app was entered when the tap was captured. `unknown` is explicit:
// a client too old to report it must be distinguishable from a cold start.
export const NOTIFICATION_LAUNCH_KIND = ['cold', 'warm', 'unknown'] as const;
export const notificationLaunchKindSchema = z.enum(NOTIFICATION_LAUNCH_KIND);
export type NotificationLaunchKind = z.infer<typeof notificationLaunchKindSchema>;

// Where a tapped notification landed, as charted on the client's open record.
// One value per way a tap can end: it reached its destination (`routed`), its
// destination was a sheet that never came up (`not_presented`), a journey gate
// took it elsewhere (`gated`), its link was unusable on this client
// (`rejected`), it was dropped before resolving (`dropped`), or the push carried
// no destination at all (`no_destination`). Shared so a future server-side
// producer of the same open cannot widen the dimension on its own.
export const NOTIFICATION_OPEN_LANDING_OUTCOME = [
  'routed',
  'not_presented',
  'gated',
  'rejected',
  'dropped',
  'no_destination',
] as const;
export const notificationOpenLandingOutcomeSchema = z.enum(NOTIFICATION_OPEN_LANDING_OUTCOME);
export type NotificationOpenLandingOutcome = z.infer<typeof notificationOpenLandingOutcomeSchema>;

// Request header a voice-session mint carries when the CLIENT already holds the
// open record for the notification handle in the same request. A server that
// sees it still resolves the handle (seeding, freshness, ownership) but leaves
// the open to the client, so one tap is never recorded by both. A header, not a
// body field: both mint bodies are `.strict()`, and an unknown body key would
// fail the mint on a server that predates it, whereas an unknown header is
// ignored.
export const NOTIFICATION_OPEN_RECORDER_HEADER = 'X-Notification-Open-Recorder';
export const NOTIFICATION_OPEN_RECORDER_CLIENT = 'client';

// What opened a voice session. Derived server-side from whether a launch
// handle resolved — never read off the request.
export const VOICE_LAUNCH_SOURCE = ['organic', 'notification'] as const;
export const voiceLaunchSourceSchema = z.enum(VOICE_LAUNCH_SOURCE);
export type VoiceLaunchSource = z.infer<typeof voiceLaunchSourceSchema>;

export type NotificationIntentStatus = z.infer<typeof notificationIntentStatusSchema>;

// Delivery transports. `push` ships today; `email` (SES) is a future sibling
// provider behind the same seam. Iterable is NOT a channel — it delivers
// outside this pipeline (see the tech design §11.3).
export const NOTIFICATION_CHANNEL = ['push', 'email'] as const;
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNEL);
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;

// The member-facing categories whose on/off lives in
// `member_notification_preferences` — the table's `category_key` CHECK and
// every wire schema for that column derive from this. It is the
// preference-backed subset of the category catalog in `@galapagos/notifications`
// (which depends on this package, so this list cannot be derived from it; a
// test there asserts the two agree). A reminder-backed category is absent on
// purpose: its reminder row owns its state, so a preference row for it must
// be unrepresentable. Adding a category here is a migration.
export const MEMBER_NOTIFICATION_PREFERENCE_CATEGORY_KEY = [
  'sona_follow_ups',
  'weekly_plans',
] as const;
export const memberNotificationPreferenceCategoryKeySchema = z.enum(
  MEMBER_NOTIFICATION_PREFERENCE_CATEGORY_KEY,
);
export type MemberNotificationPreferenceCategoryKey = z.infer<
  typeof memberNotificationPreferenceCategoryKeySchema
>;

// Who set a stored preference. Only the member today; the column exists so a
// later support- or ops-driven write stays distinguishable from the member's
// own, and widening it is a migration.
export const MEMBER_NOTIFICATION_PREFERENCE_SOURCE = ['member'] as const;
export const memberNotificationPreferenceSourceSchema = z.enum(
  MEMBER_NOTIFICATION_PREFERENCE_SOURCE,
);
export type MemberNotificationPreferenceSource = z.infer<
  typeof memberNotificationPreferenceSourceSchema
>;

// How one decision run resolved, as the closed vocabulary shared by the
// producer and the analytics forwarder's allowed-key registry.
//
// Coarser than the producer's internal outcome classification on purpose: this
// is the chartable dimension, so it distinguishes the answers a product
// question can be asked about ("how often does the model decline?") and folds
// away the ones that are operational restatements of at-least-once delivery.
// `duplicate` is kept because it is the only one of those whose rising rate
// should prompt a look.
export const NOTIFICATION_DECISION_DISPOSITION = [
  // A decision row was written and an intent queued.
  'generated',
  // The model read the transcript and judged no follow-up warranted.
  'declined',
  // Nothing usable to decide on — the conversation had no readable turns.
  'no_transcript',
  // Already decided for this conversation; the earlier decision stands.
  'duplicate',
  // The member's latest Sona consent decision is a decline: Sona-initiated
  // reach-out is withheld until they re-consent, so no transcript is read.
  'consent_declined',
] as const;
export const notificationDecisionDispositionSchema = z.enum(NOTIFICATION_DECISION_DISPOSITION);
export type NotificationDecisionDisposition = z.infer<typeof notificationDecisionDispositionSchema>;

// Why an intent was cancelled, as carried on the `notification.cancelled`
// domain event and its metric. Shared rather than sweeper-local because the
// analytics forwarder's property registry must agree on it, or a new reason
// drops as an unsafe property instead of charting. It covers every reason any
// cancel path writes — the sweeper's validation and delivery cancels, and the
// call-reminder scheduler's and reminder producer's `cancelNotification`
// calls — so no live cancel can fail to publish for want of a value.
// `cancelNotification` takes this type, so a new cancel reason cannot be
// written without joining the vocabulary. The status log's own `reason` stays a
// free string.
export const NOTIFICATION_CANCEL_REASON = [
  'operator_disabled',
  'expired',
  'unknown_definition',
  'no_content',
  'decision_missing',
  'no_email',
  'appointment_cancelled',
  'moved_inside_window',
  'cancelled_by_schedule_edit',
  'preference_opted_out',
  'no_device',
  // Member holds only VoIP-only device rows (PushKit without Expo permission).
  'voip_only',
] as const;
export const notificationCancelReasonSchema = z.enum(NOTIFICATION_CANCEL_REASON);
export type NotificationCancelReason = z.infer<typeof notificationCancelReasonSchema>;

// The APNs/FCM environment a raw native token belongs to. Client-derived
// (a TestFlight build mints production-APNs tokens, a dev build sandbox ones),
// captured from Phase 1 for Iterable forward-compat.
export const NATIVE_PUSH_PROVIDER = ['apns', 'apns_sandbox', 'fcm'] as const;
export const nativePushProviderSchema = z.enum(NATIVE_PUSH_PROVIDER);
export type NativePushProvider = z.infer<typeof nativePushProviderSchema>;

// PushKit VoIP token environment — a subset of native push providers (no FCM).
export const VOIP_TOKEN_ENVIRONMENT = ['apns', 'apns_sandbox'] as const;
export const voipTokenEnvironmentSchema = z.enum(VOIP_TOKEN_ENVIRONMENT);
export type VoipTokenEnvironment = z.infer<typeof voipTokenEnvironmentSchema>;

// Generic (non-clinical) copy carried inline on an intent. Clinical/personalized
// copy never lives here — it stays in notification_decisions (Phase 1b).
export const notificationContentSchema = z
  .object({
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(1000),
  })
  .strict();
export type NotificationContent = z.infer<typeof notificationContentSchema>;

// ADR-035 deeplink target that rides the push payload so the client can
// navigate on tap. Validated against the shared ScreenId registry and the
// per-screen param contract — the same shape reminders persist (ADR-077 Rule
// 5), deliberately not a looser sibling.
//
// Validated at write time even though Phase 1a has no client consumer: the
// column outlives the gap, and a destination stored against an unknown screen
// or with params no route contract can resolve is undiscoverable until the tap
// path lands and it silently dead-ends.
export const notificationDestinationSchema = z
  .object({
    screenId: screenIdSchema,
    params: z.record(z.string(), z.string()).default({}),
  })
  .strict()
  .superRefine((destination, ctx) => {
    const parsed = screenParamsSchemas[destination.screenId].strict().safeParse(destination.params);
    if (!parsed.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['params'],
        message: 'params do not match the ScreenId contract',
      });
    }
  });
export type NotificationDestination = z.infer<typeof notificationDestinationSchema>;

// The closed set of reasons an intent reaches a TERMINAL status (`failed` or
// `cancelled`). Deliberately excludes the defer reasons (`kill_switch`, `cap`,
// `provider_retryable`), which share the log-entry shape but leave the intent
// live and are not a dispatch outcome.
//
// It lives here, rather than beside the sweeper that writes it, because
// `@galapagos/observability` mirrors it as a metric-tag value constraint and
// drift-guards the mirror against this source. That package sits below the api
// workspace, so the guard can read a sibling package but never an app.
export const DISPATCH_TERMINAL_REASONS = [
  'operator_disabled',
  'unknown_definition',
  'expired',
  'no_content',
  'decision_missing',
  'no_email',
  'no_device',
  'voip_only',
  'provider_failed',
  'preference_opted_out',
] as const;
export const dispatchTerminalReasonSchema = z.enum(DISPATCH_TERMINAL_REASONS);
export type DispatchTerminalReason = (typeof DISPATCH_TERMINAL_REASONS)[number];

// Append-only status-transition history on the intent row (replaces a separate
// state-events table). A short-circuited validation check is OMITTED from
// `checks`, never fabricated — validation runs in order and stops at the first
// reject.
export const intentStatusLogEntrySchema = z
  .object({
    at: z.string(),
    to: notificationIntentStatusSchema,
    reason: z.string().optional(),
    detail: z
      .discriminatedUnion('kind', [
        z
          .object({
            kind: z.literal('validation'),
            checks: z
              .object({
                expiry: z.enum(['pass', 'fail']),
                killSwitch: z.enum(['pass', 'fail']),
                // The member's category preference. Absent, never 'pass', for
                // a definition that is exempt from member preference: the
                // check was not run, so there is no result to record.
                preference: z.enum(['pass', 'fail']),
                cap: z.enum(['pass', 'fail']),
              })
              .partial(),
          })
          .strict(),
        z
          .object({
            kind: z.literal('delivery'),
            channel: z.string(),
            providerResult: z.unknown(),
            dryRun: z.boolean(),
            /**
             * Provider error codes behind a failed send, deduplicated — e.g.
             * `DeviceNotRegistered`, `MessageRateExceeded`. Codes only, never a
             * message or token, so this stays safe to persist and to read back.
             * Without it a delivery failure is indistinguishable from any other
             * on the row, which is how a broken send path stayed invisible.
             */
            providerErrors: z.array(z.string()).optional(),
          })
          .strict(),
      ])
      .optional(),
  })
  .strict();
export type IntentStatusLogEntry = z.infer<typeof intentStatusLogEntrySchema>;

// Full LLM response retained on a decision row for audit (Phase 1b). Kept loose
// — the shape is model/prompt-version dependent and is never rendered from.
export const decisionRawOutputSchema = z.record(z.string(), z.unknown());
export type DecisionRawOutput = z.infer<typeof decisionRawOutputSchema>;
